import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../../utils/api';
import { formatCurrency, formatDate } from '../../utils/invoiceUtils';

export default function DocumentLinesModal({ document, kind, onClose }) {
  const [state, setState] = useState({ loading: true, lines: [], error: '' });
  const dialogRef = useRef(null);
  const isBudget = kind === 'budgets';
  const label = isBudget ? 'presupuesto' : 'factura';

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setState({ loading: true, lines: [], error: '' });
    async function load() {
      try {
        const res = await apiFetch(`/api/clients/${kind}/${document.id}/lines`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal
        });
        const data = await parseJsonSafe(res);
        if (!active) return;
        if (res.status === 401) return handleUnauthorized();
        if (!res.ok) throw new Error(data.message || 'No se pudieron cargar las líneas.');
        const lines = Array.isArray(data) ? data : data?.lines;
        if (!Array.isArray(lines)) throw new Error('Respuesta de líneas no válida.');
        setState({ loading: false, lines, error: '' });
      } catch (err) {
        if (active && err.name !== 'AbortError') setState({ loading: false, lines: [], error: err.message });
      }
    }
    load();
    return () => { active = false; controller.abort(); };
  }, [document.id, kind]);

  useEffect(() => {
    const previousFocus = window.document.activeElement;
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    dialogRef.current.focus();
    return () => {
      window.document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, []);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') { event.stopPropagation(); onClose(); }
    if (event.key === 'Tab') {
      const buttons = dialogRef.current.querySelectorAll('button');
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && (window.document.activeElement === first || window.document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (window.document.activeElement === last || window.document.activeElement === dialogRef.current)) {
        event.preventDefault(); first.focus();
      }
    }
  };

  return createPortal(
    <div className="modal show" style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }} onClick={(event) => { if (event.target === event.currentTarget) onClose(); }} onKeyDown={onKeyDown}>
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content" role="dialog" aria-modal="true" aria-labelledby="document-lines-title" tabIndex={-1} ref={dialogRef}>
          <div className="modal-header">
            <h5 className="modal-title" id="document-lines-title">Líneas de {label} {document.numero || document.presupuesto || `#${document.id}`}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          <div className="modal-body">
            {document.razon_social && <p className="fw-semibold">{document.razon_social}</p>}
            {state.loading ? <div role="status">Cargando líneas…</div> : state.error ? <div className="alert alert-danger" role="alert">{state.error}</div> : state.lines.length === 0 ? <p className="text-muted">No hay líneas en este {label}.</p> : (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead><tr><th>Concepto</th><th className="text-end">Cantidad</th><th className="text-end">Precio</th><th className="text-end">Descuento</th><th className="text-end">IVA</th><th className="text-end">Importe sin IVA</th></tr></thead>
                  <tbody>{state.lines.map((line, index) => (
                    <tr key={line.id ?? index}>
                      <td style={{ whiteSpace: 'pre-wrap', minWidth: 200 }}>{line.concepto}{(line.period_start || line.period_end) && <div className="small text-muted">{formatDate(line.period_start)} — {formatDate(line.period_end)}</div>}</td>
                      <td className="text-end">{line.cantidad}</td>
                      <td className="text-end text-nowrap">{formatCurrency(line.precio)}</td>
                      <td className="text-end">{Number(line.descuento || 0)}%</td>
                      <td className="text-end">{Number(line.tva_tx ?? 21)}%</td>
                      <td className="text-end text-nowrap">{formatCurrency(Number(line.total ?? (Number(line.cantidad) * Number(line.precio))) * (isBudget ? (1 - Number(line.descuento || 0) / 100) : 1))}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button></div>
        </div>
      </div>
    </div>, window.document.body
  );
}
